import { usePrivateMode } from "@/lib/privateMode";
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { MouseSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useRemoteSession } from "@/hooks/useRemoteSession";
import { useRemoteInput } from "@/hooks/useRemoteInput";
import { SessionToolbar } from "@/components/session/SessionToolbar";
import { MachineDetailsPanel } from "@/components/session/MachineDetailsPanel";

export default function Session() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const priv = usePrivateMode();
  const { currentOrg } = useAuth();
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [machine, setMachine] = useState(null);
  const [controlEnabled, setControlEnabled] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [qualityMode, setQualityMode] = useState("auto");   // auto | standard | balanced | sharp
  const [autoLevel, setAutoLevel] = useState("standard");    // current preset while in auto mode

  const { frame, setFrame, status, fps, blocked, sameNetwork, send } = useRemoteSession(machineId);
  const input = useRemoteInput({ imgRef, send, controlEnabled, status });
  const [showSelfWarn, setShowSelfWarn] = useState(false);

  const effectiveQuality = qualityMode === "auto" ? autoLevel : qualityMode;

  // Push the effective preset to the agent whenever it changes, and re-push after a reconnect
  // (the agent may have restarted on its installed default). Cheap: only fires on change.
  const lastSent = useRef(null);
  useEffect(() => {
    if (status !== "online") { lastSent.current = null; return; }
    if (lastSent.current !== effectiveQuality) {
      lastSent.current = effectiveQuality;
      send({ type: "set_quality", quality: effectiveQuality });
    }
  }, [effectiveQuality, status, send]);

  // Adaptive controller: in Auto mode, watch received FPS and step quality down on a slow link,
  // back up when it recovers. Hysteresis + warmup prevent oscillation. Agent streams at a fixed
  // 5fps, so a sustained low received-FPS means the connection can't keep up.
  const fpsRef = useRef(0);
  useEffect(() => { fpsRef.current = fps; }, [fps]);
  useEffect(() => {
    if (qualityMode !== "auto") return;
    const order = ["standard", "balanced", "sharp"];
    let good = 0;
    const startedAt = Date.now();
    const id = setInterval(() => {
      if (status !== "online") { good = 0; return; }
      if (Date.now() - startedAt < 4000) return; // warmup after (re)entering auto
      const f = fpsRef.current;
      setAutoLevel((cur) => {
        const idx = order.indexOf(cur);
        if (f > 0 && f < 2.5) { good = 0; return idx > 0 ? order[idx - 1] : cur; }  // struggling → down
        if (f >= 4.5) { good += 1; if (good >= 4 && idx < 2) { good = 0; return order[idx + 1]; } return cur; }  // healthy → up (slowly)
        good = 0; return cur;
      });
    }, 2000);
    return () => clearInterval(id);
  }, [qualityMode, status]);

  useEffect(() => {
    if (!currentOrg) return;
    api.get(`/orgs/${currentOrg.org_id}/machines/${machineId}`).then((r) => setMachine(r.data)).catch(() => {
      toast.error("Machine not found");
      navigate("/dashboard");
    });
  }, [machineId, currentOrg, navigate]);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // Leaving the session should never strand the user in fullscreen — exit it on unmount.
  useEffect(() => {
    return () => { if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); };
  }, []);

  const disconnect = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    navigate("/dashboard");
  };

  // Same public IP as the agent (often the SAME computer) → warn before the mirror loop bites,
  // unless the user already confirmed it's a different computer for this machine.
  useEffect(() => {
    if (sameNetwork && localStorage.getItem(`controlly_samenet_ok_${machineId}`) !== "1") {
      setShowSelfWarn(true);
    }
  }, [sameNetwork, machineId]);

  const onSetQuality = (mode) => {
    setQualityMode(mode);
    if (mode === "auto") {
      toast.success("Streaming quality: Auto — adapts to your connection");
    } else {
      toast.success(`Streaming quality: ${mode === "sharp" ? "Sharp (HD)" : mode === "balanced" ? "Balanced" : "Bandwidth-friendly"}`);
    }
  };

  const StatusPill = () => (
    <span className={`inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
      status === "online" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-red-400 border-red-500/30 bg-red-500/10"
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === "online" ? "bg-emerald-400 animate-pulse" : "bg-red-400"}`} />
      {status === "online" ? "Live" : status === "connecting" ? "Connecting" : "Offline"}
    </span>
  );

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Top bar */}
      <div className="h-12 border-b border-white/10 flex items-center justify-between px-4 shrink-0 bg-zinc-950">
        <div className="flex items-center gap-3">
          <span className="font-heading font-semibold text-sm">{machine?.name || "…"}</span>
          <StatusPill />
        </div>
        <div className="flex items-center gap-2 font-mono text-xs text-zinc-500">
          <MouseSimple size={14} /> {fps} fps
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative session-grid-bg overflow-hidden">
        <div
          ref={canvasRef}
          data-testid="remote-canvas"
          className="absolute inset-0 flex items-center justify-center"
          style={{ cursor: "default" }}
          onMouseMove={input.onMouseMove}
          onClick={input.onClick}
          onContextMenu={input.onContextMenu}
          onDoubleClick={input.onDoubleClick}
          onWheel={input.onWheel}
        >
          {frame ? (
            <img ref={imgRef} src={frame} alt="remote screen" className="w-full h-full object-contain select-none pointer-events-none" draggable={false} />
          ) : (
            <div className="text-center max-w-sm px-4">
              {blocked ? (
                <>
                  <div className="font-heading text-base font-semibold text-red-400">Connection refused</div>
                  <p className="text-sm text-zinc-400 mt-2">{blocked.message}</p>
                  {blocked.code === 4004 && !priv && (
                    <button data-testid="go-billing-button" onClick={() => navigate("/billing")} className="mt-4 px-4 h-9 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors">
                      View plans
                    </button>
                  )}
                </>
              ) : (
                <>
                  <div className="font-mono text-sm text-zinc-500 animate-pulse">
                    {status === "online" ? "Receiving screen…" : status === "connecting" ? "Connecting to machine…" : "Machine is offline"}
                  </div>
                  {status === "offline" && (
                    <p className="text-xs text-zinc-600 mt-2 max-w-xs mx-auto">Make sure the agent is running on the remote computer.</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <SessionToolbar
          onDisconnect={disconnect}
          controlEnabled={controlEnabled}
          onToggleControl={() => setControlEnabled((v) => !v)}
          onFullscreen={goFullscreen}
          onClearFrame={() => setFrame(null)}
          panelOpen={panelOpen}
          onTogglePanel={() => setPanelOpen((v) => !v)}
          quality={qualityMode}
          effectiveQuality={effectiveQuality}
          onSetQuality={onSetQuality}
          onSendKeys={(keys) => send({ type: "hotkey", keys })}
        />

        <MachineDetailsPanel open={panelOpen} onClose={() => setPanelOpen(false)} machine={machine} status={status} />
      </div>

      {showSelfWarn && (
        <div data-testid="self-connect-warning" className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-zinc-900 border border-amber-500/40 rounded-xl p-6 shadow-2xl">
            <div className="font-heading text-lg font-bold text-amber-400">Is this the computer you're using right now?</div>
            <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
              <span className="font-semibold text-white">{machine?.name}</span> appears to be on the same network as this device. If it's the <span className="text-white font-semibold">same computer</span>, controlling it from itself creates an endless “screen-within-a-screen” mirror that's hard to escape.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <button
                data-testid="self-warn-disconnect"
                onClick={disconnect}
                className="flex-1 h-10 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-sm font-bold transition-colors"
              >
                Disconnect (it's this computer)
              </button>
              <button
                data-testid="self-warn-continue"
                onClick={() => { localStorage.setItem(`controlly_samenet_ok_${machineId}`, "1"); setShowSelfWarn(false); }}
                className="flex-1 h-10 rounded-md bg-transparent border border-white/15 hover:bg-zinc-800 text-zinc-200 text-sm font-medium transition-colors"
              >
                It's a different computer — continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
